/*
 * Browser-side twin of the C# HVACloud.Core.Hmi.Schema.InMemoryPointBindingSource.
 * Used by the HMI-preview page (and later by widget-catalog previews / snapshot
 * tests) so a widget component can be exercised in isolation without a live
 * hub connection.
 *
 * Semantics mirror the interface exactly: subscribe emits cached-then-updates,
 * write returns pending-then-terminal, history is a ring-buffer snapshot.
 * The one difference vs. the C# source: this class also exposes a small
 * `simulate` helper for the preview page so the demo values move.
 *
 * Zusatz 2026-09-03: `publish` nimmt jetzt optional eine Empfangszeit
 * (`receivedAt`). Der Grund ist kein Simulator-Selbstzweck, sondern eine
 * Prüfbarkeitslücke: der Live-Adapter leitet `stale` aus dem ALTER eines
 * Werts ab, dieser Simulator wiederholt aber nie einen unveränderten Wert
 * und stempelt immer die Jetzt-Zeit. Damit konnte niemand ansehen, wie eine
 * Kachel im veralteten Zustand aussieht — und das ausgerechnet vor dem
 * ersten Feldeinsatz. Mit einer rückdatierbaren Empfangszeit kann die
 * Katalog-Seite jeden degradierten Zustand vorführen.
 */

import { correlationId as newCorrelationId } from './correlationId';
import type {
  IPointBindingSource,
  PointValue,
  PointQuality,
  WriteAcknowledgement,
  PointHistorySample,
  HistoryAggregation,
} from './binding';

interface Subscriber {
  push(value: PointValue): void;
}

interface PointState {
  current: PointValue | null;
  history: PointValue[];
  subscribers: Set<Subscriber>;
}

export interface InMemorySourceOptions {
  /** How long to hold a write in the Pending state before echoing it back
   *  as Confirmed. Simulates the round-trip a real driver would take. */
  echoLatencyMs?: number;
  /** How many samples the ring buffer retains per point. */
  historyRingSize?: number;
}

export class InMemoryPointBindingSource implements IPointBindingSource {
  private readonly points = new Map<string, PointState>();
  private readonly echoLatencyMs: number;
  private readonly historyRingSize: number;

  constructor(opts: InMemorySourceOptions = {}) {
    this.echoLatencyMs = opts.echoLatencyMs ?? 50;
    this.historyRingSize = opts.historyRingSize ?? 10_000;
  }

  /** Seed a point with a starting value. Idempotent. */
  seed(pointId: string, value: unknown, quality: PointQuality = 'good'): void {
    this.publishInternal(pointId, value, quality);
  }

  /**
   * Push an update from a simulator loop.
   *
   * `options.receivedAt` datiert die Empfangszeit zurück, damit sich das
   * Alters-Verhalten der Live-Quelle im Katalog vorführen lässt (siehe
   * Datei-Kopf). Ohne Angabe gilt die Jetzt-Zeit.
   */
  publish(
    pointId: string,
    value: unknown,
    quality: PointQuality = 'good',
    options?: { receivedAt?: number },
  ): void {
    this.publishInternal(pointId, value, quality, options?.receivedAt);
  }

  /**
   * Alle bekannten Punkte mit ihrem aktuellen Wert erneut ausliefern, aber
   * mit anderer Qualität und/oder Empfangszeit.
   *
   * Die Katalog-Seite ruft das beim Umschalten des Zustands-Wählers auf:
   * ein Umschalten von "live" auf "veraltet" soll sofort wirken und nicht
   * erst beim nächsten Sim-Tick, sonst wirkt der Schalter kaputt.
   */
  republishAll(quality: PointQuality, options?: { receivedAt?: number }): void {
    for (const [pointId, state] of this.points) {
      if (!state.current) continue;
      this.publishInternal(pointId, state.current.value, quality, options?.receivedAt);
    }
  }

  async *subscribe(pointId: string, signal: AbortSignal): AsyncIterable<PointValue> {
    const state = this.ensure(pointId);
    // Emit cached value first so a fresh subscription doesn't wait for the
    // next tick to have something to show.
    if (state.current) yield state.current;

    const queue: PointValue[] = [];
    let waker: (() => void) | null = null;
    const subscriber: Subscriber = {
      push(v) {
        queue.push(v);
        waker?.();
      },
    };
    state.subscribers.add(subscriber);

    const abortHandler = () => waker?.();
    signal.addEventListener('abort', abortHandler);

    try {
      while (!signal.aborted) {
        while (queue.length > 0 && !signal.aborted) {
          const next = queue.shift()!;
          yield next;
        }
        if (signal.aborted) break;
        await new Promise<void>((resolve) => {
          waker = resolve;
        });
        waker = null;
      }
    } finally {
      state.subscribers.delete(subscriber);
      signal.removeEventListener('abort', abortHandler);
    }
  }

  async *write(pointId: string, value: unknown, signal: AbortSignal): AsyncIterable<WriteAcknowledgement> {
    const correlationId = newCorrelationId();
    const at = new Date().toISOString();
    yield { correlationId, state: 'pending', echoValue: null, at };

    try {
      await new Promise<void>((resolve, reject) => {
        const timer = setTimeout(() => resolve(), this.echoLatencyMs);
        signal.addEventListener('abort', () => {
          clearTimeout(timer);
          reject(new DOMException('aborted', 'AbortError'));
        }, { once: true });
      });
    } catch {
      return;
    }

    this.publish(pointId, value);
    yield {
      correlationId,
      state: 'confirmed',
      echoValue: value,
      at: new Date().toISOString(),
    };
  }

  async getHistory(
    pointId: string,
    since: Date,
    until: Date,
    _aggregation: HistoryAggregation,
    _signal: AbortSignal,
  ): Promise<PointHistorySample[]> {
    const state = this.points.get(pointId);
    if (!state) return [];
    const sinceMs = since.getTime();
    const untilMs = until.getTime();
    return state.history
      .filter((v) => {
        const ts = Date.parse(v.timestamp);
        return ts >= sinceMs && ts <= untilMs;
      })
      .map((v) => ({
        timestamp: v.timestamp,
        value: typeof v.value === 'number' ? v.value
             : v.value == null ? null
             : Number(v.value),
        quality: v.quality,
      }));
  }

  private ensure(pointId: string): PointState {
    let state = this.points.get(pointId);
    if (!state) {
      state = { current: null, history: [], subscribers: new Set() };
      this.points.set(pointId, state);
    }
    return state;
  }

  private publishInternal(
    pointId: string,
    value: unknown,
    quality: PointQuality,
    receivedAt?: number,
  ): void {
    const state = this.ensure(pointId);
    // Empfangszeit ist normalerweise die Erzeugungszeit. Das Feld MUSS
    // gesetzt sein, weil die Alters-Auswertung (`hmi/quality.ts`) darauf
    // beruht. Rückdatierbar für die Zustands-Vorführung im Katalog.
    const stamped = receivedAt ?? Date.now();
    const sample: PointValue = {
      value,
      quality,
      timestamp: new Date(stamped).toISOString(),
      receivedAt: stamped,
    };
    state.current = sample;
    state.history.push(sample);
    while (state.history.length > this.historyRingSize) state.history.shift();
    for (const s of state.subscribers) s.push(sample);
  }
}
